import React, { useEffect, useState, useRef } from 'react';
import { merchantApi } from '../../api/config';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSave, faCog, faPalette, faImage, faPercent, faMoneyBill, faFont, faCheck,
  faRotate, faEyeDropper, faSwatchbook, faSliders, faDroplet, faShieldAlt, faExternalLinkAlt,
  faVolumeUp, faCloudUpload, faPlay, faPause, faTimes, faMusic, faReceipt, faKey, faToggleOn, faToggleOff
} from '@fortawesome/free-solid-svg-icons';
import { useTheme } from '../../contexts/ThemeContext';
import FinanceControlSection from '../../components/admin/FinanceControlSection';

const colorPalettes = [
  { name: 'Sunset Orange', colors: { primary: '#ff6b35', secondary: '#f7931e', accent: '#667eea', bg: '#f5f7fa', text: '#1f2937' } },
  { name: 'Ocean Blue',    colors: { primary: '#0ea5e9', secondary: '#06b6d4', accent: '#8b5cf6', bg: '#f0f9ff', text: '#0f172a' } },
  { name: 'Forest Green',  colors: { primary: '#22c55e', secondary: '#10b981', accent: '#f59e0b', bg: '#f0fdf4', text: '#14532d' } },
  { name: 'Royal Purple',  colors: { primary: '#8b5cf6', secondary: '#a855f7', accent: '#ec4899', bg: '#faf5ff', text: '#1e1b4b' } },
  { name: 'Rose Gold',     colors: { primary: '#f43f5e', secondary: '#ec4899', accent: '#f59e0b', bg: '#fff1f2', text: '#881337' } },
  { name: 'Midnight Dark', colors: { primary: '#6366f1', secondary: '#8b5cf6', accent: '#22d3ee', bg: '#0f172a', text: '#f8fafc' } },
  { name: 'Warm Amber',    colors: { primary: '#f59e0b', secondary: '#eab308', accent: '#ef4444', bg: '#fffbeb', text: '#78350f' } },
  { name: 'Cool Slate',    colors: { primary: '#64748b', secondary: '#475569', accent: '#0ea5e9', bg: '#f8fafc', text: '#1e293b' } },
];

const colorPresets = {
  primary:   ['#ff6b35', '#ef4444', '#f59e0b', '#22c55e', '#0ea5e9', '#6366f1', '#8b5cf6', '#ec4899'],
  secondary: ['#f7931e', '#fbbf24', '#34d399', '#22d3ee', '#818cf8', '#a855f7', '#f472b6', '#fb923c'],
  accent:    ['#667eea', '#8b5cf6', '#06b6d4', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#6366f1'],
  bg:        ['#f5f7fa', '#f0f9ff', '#f0fdf4', '#faf5ff', '#fff1f2', '#fffbeb', '#f8fafc', '#0f172a'],
  text:      ['#1f2937', '#0f172a', '#14532d', '#1e1b4b', '#881337', '#78350f', '#1e293b', '#f8fafc'],
};

const ColorPicker: React.FC<{
  value: string;
  onChange: (color: string) => void;
  label: string;
  presets: string[];
}> = ({ value, onChange, label, presets }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tempColor, setTempColor] = useState(value);
  const pickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => { setTempColor(value); }, [value]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleConfirm = () => { onChange(tempColor); setIsOpen(false); };

  return (
    <div className="relative" ref={pickerRef}>
      <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1.5">{label}</label>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center gap-3 p-3 bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-xl hover:border-gray-300 dark:hover:border-slate-500 hover:shadow-md transition-all duration-200 group"
      >
        <div className="w-12 h-12 rounded-xl shadow-inner border-2 border-white ring-2 ring-gray-100 dark:ring-slate-600 group-hover:scale-105 transition-transform"
             style={{ backgroundColor: value || '#ffffff' }} />
        <div className="flex-1 text-left">
          <p className="font-mono text-sm font-semibold text-gray-700 dark:text-slate-200 uppercase">{value || '#ffffff'}</p>
          <p className="text-[10px] text-gray-400 dark:text-slate-500 flex items-center gap-1">
            <FontAwesomeIcon icon={faEyeDropper} /> คลิกเพื่อเลือกสี
          </p>
        </div>
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-2 p-4 bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-gray-100 dark:border-slate-700 w-80 max-w-[calc(100vw-2rem)] left-0">
          <div className="mb-4">
            <label className="flex items-center gap-1 text-xs font-medium text-gray-500 dark:text-slate-400 mb-2">
              <FontAwesomeIcon icon={faSwatchbook} /> สีแนะนำ
            </label>
            <div className="grid grid-cols-8 gap-2">
              {presets.map((color, idx) => (
                <button key={idx} onClick={() => setTempColor(color)}
                  className={`w-8 h-8 rounded-lg transition-all duration-150 hover:scale-110 shadow-sm
                    ${tempColor === color ? 'ring-2 ring-offset-2 ring-orange-500 scale-110' : 'hover:ring-2 hover:ring-gray-300'}`}
                  style={{ backgroundColor: color }} title={color} />
              ))}
            </div>
          </div>

          <div className="mb-4">
            <label className="flex items-center gap-1 text-xs font-medium text-gray-500 dark:text-slate-400 mb-2">
              <FontAwesomeIcon icon={faDroplet} /> เลือกสีเอง
            </label>
            <div className="flex items-center gap-3">
              <input type="color" value={tempColor} onChange={(e) => setTempColor(e.target.value)}
                className="w-16 h-16 rounded-xl cursor-pointer border-2 border-gray-200 dark:border-slate-600 p-1" />
              <input type="text" value={tempColor} onChange={(e) => setTempColor(e.target.value)}
                className="flex-1 px-4 py-3 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-xl font-mono text-sm uppercase focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200"
                placeholder="#000000" />
            </div>
          </div>

          <div className="flex items-center gap-3 pt-3 border-t border-gray-100 dark:border-slate-700">
            <div className="flex-1 flex items-center gap-2">
              <div className="w-10 h-10 rounded-lg shadow-inner border border-gray-200 dark:border-slate-600" style={{ backgroundColor: value }} />
              <span className="text-gray-300 dark:text-slate-500 text-lg">→</span>
              <div className="w-10 h-10 rounded-lg shadow-lg border-2 border-white ring-1 ring-gray-200 dark:ring-slate-600" style={{ backgroundColor: tempColor }} />
            </div>
            <button onClick={handleConfirm}
              className="px-5 py-2.5 text-white rounded-xl text-sm font-semibold hover:shadow-lg transition-all flex items-center gap-2"
              style={{ background: 'var(--theme-primary-gradient)' }}>
              <FontAwesomeIcon icon={faCheck} /> ใช้สีนี้
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const SoundSettings: React.FC<{
  depositUrl: string;
  withdrawalUrl: string;
  onUploadSuccess: () => void;
}> = ({ depositUrl, withdrawalUrl, onUploadSuccess }) => {
  const [uploading, setUploading] = useState<'deposit' | 'withdrawal' | null>(null);
  const [playingSound, setPlayingSound] = useState<'deposit' | 'withdrawal' | null>(null);
  const depositAudioRef = useRef<HTMLAudioElement | null>(null);
  const withdrawalAudioRef = useRef<HTMLAudioElement | null>(null);
  const depositInputRef = useRef<HTMLInputElement>(null);
  const withdrawalInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>, type: 'deposit' | 'withdrawal') => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowedTypes = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/ogg', 'audio/webm'];
    if (!allowedTypes.includes(file.type)) { toast.error('กรุณาเลือกไฟล์เสียง (MP3, WAV, OGG, WebM) เท่านั้น'); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error('ไฟล์มีขนาดใหญ่เกิน 10MB'); return; }
    try {
      setUploading(type);
      const formData = new FormData();
      formData.append('sound', file);
      const response = await merchantApi.post(`/admin/system/upload-sound?type=${type}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (response.data.success) { toast.success(`อัพโหลดเสียง${type === 'deposit' ? 'ฝากเงิน' : 'ถอนเงิน'}สำเร็จ`); onUploadSuccess(); }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'เกิดข้อผิดพลาดในการอัพโหลด');
    } finally {
      setUploading(null);
      if (type === 'deposit' && depositInputRef.current) depositInputRef.current.value = '';
      else if (type === 'withdrawal' && withdrawalInputRef.current) withdrawalInputRef.current.value = '';
    }
  };

  const playSound = async (type: 'deposit' | 'withdrawal') => {
    const url = type === 'deposit' ? depositUrl : withdrawalUrl;
    if (!url) { toast.warning('ยังไม่ได้ตั้งค่าเสียง'); return; }
    if (depositAudioRef.current) { depositAudioRef.current.pause(); depositAudioRef.current.currentTime = 0; }
    if (withdrawalAudioRef.current) { withdrawalAudioRef.current.pause(); withdrawalAudioRef.current.currentTime = 0; }
    try {
      const audio = new Audio(url);
      if (type === 'deposit') depositAudioRef.current = audio;
      else withdrawalAudioRef.current = audio;
      audio.onerror = () => { setPlayingSound(null); toast.error('ไม่สามารถเล่นเสียงได้'); };
      audio.onended = () => setPlayingSound(null);
      setPlayingSound(type);
      await audio.play();
    } catch { setPlayingSound(null); toast.error('ไม่สามารถเล่นเสียงได้'); }
  };

  const stopSound = (type: 'deposit' | 'withdrawal') => {
    const audio = type === 'deposit' ? depositAudioRef.current : withdrawalAudioRef.current;
    if (audio) { audio.pause(); audio.currentTime = 0; }
    setPlayingSound(null);
  };

  const SoundCard = ({ type, url, color }: { type: 'deposit' | 'withdrawal'; url: string; color: 'green' | 'orange' }) => {
    const inputRef = type === 'deposit' ? depositInputRef : withdrawalInputRef;
    const isDeposit = type === 'deposit';
    const label = isDeposit ? 'เสียงแจ้งเตือนฝากเงิน' : 'เสียงแจ้งเตือนถอนเงิน';
    const sublabel = isDeposit ? 'เล่นเมื่อมีรายการฝากเงินใหม่' : 'เล่นเมื่อมีรายการถอนเงินใหม่';
    const colors = {
      green:  { bg: 'from-green-50 to-emerald-50', border: 'border-green-200', icon: 'bg-green-500', title: 'text-green-800', sub: 'text-green-600', cardBorder: 'border-green-200', fileName: 'text-green-700', dashed: 'border-green-300', btn: 'bg-green-100 text-green-700 hover:bg-green-200', hint: 'text-green-500', playOn: 'bg-green-100 text-green-700 hover:bg-green-200' },
      orange: { bg: 'from-orange-50 to-red-50',   border: 'border-orange-200', icon: 'bg-orange-500', title: 'text-orange-800', sub: 'text-orange-600', cardBorder: 'border-orange-200', fileName: 'text-orange-700', dashed: 'border-orange-300', btn: 'bg-orange-100 text-orange-700 hover:bg-orange-200', hint: 'text-orange-500', playOn: 'bg-orange-100 text-orange-700 hover:bg-orange-200' },
    }[color];

    return (
      <div className={`p-5 bg-gradient-to-br ${colors.bg} rounded-xl border ${colors.border}`}>
        <div className="flex items-center gap-2 mb-4">
          <div className={`w-8 h-8 rounded-lg ${colors.icon} flex items-center justify-center text-white`}>
            <FontAwesomeIcon icon={faMusic} className="text-sm" />
          </div>
          <div>
            <h3 className={`font-semibold ${colors.title}`}>{label}</h3>
            <p className={`text-xs ${colors.sub}`}>{sublabel}</p>
          </div>
        </div>
        {url && (
          <div className={`mb-4 p-3 bg-white dark:bg-slate-800/60 rounded-lg border ${colors.cardBorder}`}>
            <div className="flex items-center justify-between mb-2">
              <div className={`flex items-center gap-2 text-sm ${colors.fileName} truncate flex-1 mr-2`}>
                <FontAwesomeIcon icon={faCheck} className={colors.hint} />
                <span className="truncate">{url.split('/').pop()}</span>
              </div>
              <div className="flex items-center gap-2">
                <a href={url} target="_blank" rel="noopener noreferrer"
                   className="px-2 py-1 bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-slate-600 rounded text-xs" title="เปิดลิงก์">🔗</a>
                <button onClick={() => playingSound === type ? stopSound(type) : playSound(type)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center gap-1
                    ${playingSound === type ? 'bg-red-100 text-red-600 hover:bg-red-200' : colors.playOn}`}>
                  <FontAwesomeIcon icon={playingSound === type ? faPause : faPlay} />
                  {playingSound === type ? 'หยุด' : 'เล่น'}
                </button>
              </div>
            </div>
            <audio controls src={url} className="w-full h-8">Your browser does not support audio.</audio>
          </div>
        )}
        <input ref={inputRef} type="file" accept="audio/mpeg,audio/mp3,audio/wav,audio/ogg,audio/webm"
          onChange={(e) => handleFileChange(e, type)} className="hidden" />
        <button onClick={() => inputRef.current?.click()} disabled={uploading === type}
          className={`w-full py-3 border-2 border-dashed ${colors.dashed} rounded-xl ${colors.btn} transition-all duration-200 flex items-center justify-center gap-2 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed`}>
          {uploading === type ? <><FontAwesomeIcon icon={faRotate} className="animate-spin" />กำลังอัพโหลด...</> : <><FontAwesomeIcon icon={faCloudUpload} />{url ? 'เปลี่ยนไฟล์เสียง' : 'อัพโหลดไฟล์เสียง'}</>}
        </button>
        <p className={`text-[10px] ${colors.hint} mt-2 text-center`}>รองรับ MP3, WAV, OGG, WebM (ไม่เกิน 10MB)</p>
      </div>
    );
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-theme p-6 mb-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white shadow-lg">
          <FontAwesomeIcon icon={faVolumeUp} />
        </div>
        <div>
          <h2 className="font-bold text-gray-800 dark:text-slate-100">เสียงแจ้งเตือน</h2>
          <p className="text-xs text-gray-400 dark:text-slate-500">อัพโหลดไฟล์เสียงสำหรับแจ้งเตือนรายการฝาก-ถอน</p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <SoundCard type="deposit" url={depositUrl} color="green" />
        <SoundCard type="withdrawal" url={withdrawalUrl} color="orange" />
      </div>
      <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-xl">
        <p className="text-sm text-blue-800 dark:text-blue-300 mb-2"><strong>📝 หมายเหตุ:</strong></p>
        <ul className="text-xs text-blue-700 dark:text-blue-400 list-disc list-inside space-y-1">
          <li>เสียงจะเล่นอัตโนมัติเมื่อมีรายการใหม่ (ผู้ใช้ต้องเคย interact กับหน้าเว็บก่อน)</li>
          <li>แนะนำให้ใช้ไฟล์เสียงสั้นๆ (2-5 วินาที) เพื่อไม่รบกวนการทำงาน</li>
          <li>ไฟล์จะถูกอัพโหลดไปยัง Cloudflare R2 Storage</li>
        </ul>
      </div>
    </div>
  );
};

// Keys that already have dedicated UI sections — exclude from the catch-all "Other Settings"
const HANDLED_SETTING_KEYS = new Set([
  'system_logo_url',
  'order_enabled', 'order_minimum_amount', 'slip_order_expiry_minutes',
  'payout_enabled', 'min_payout_amount',
  'settlement_enabled', 'settlement_enabled_usdt', 'min_settlement_amount', 'settlement_fee_rate',
  'usdt_withdrawal_fee_rate',
  'safe_wallet_deposit_fee_rate', 'safe_wallet_withdraw_fee_rate', 'safe_wallet_min_settlement_usdt',
  'customer_slip_upload_enabled', 'allow_slip_overpay', 'allow_slip_underpay',
  'slip_verification_enabled', 'slip2go_secret_key', 'slip_verification_provider', 'easyslip_api_key',
  'turnstile_site_key',
  'sound_deposit_url', 'sound_withdrawal_url',
  // ⭐ P2P: จัดการใน P2pSettingsCard (แท็บการเงิน) — ตัดออกจาก catch-all "อื่นๆ"
  'p2p_enabled', 'deposit_enabled', 'deposit_admin_bank_id', 'p2p_pair_ttl_minutes',
  'p2p_max_matches_per_payout',
  'p2p_noncustodial_enabled', // ตรึงไว้ (ห้ามแก้จาก UI)
]);

type TabId = 'theme' | 'system' | 'finance' | 'features';

const TABS: { id: TabId; label: string; icon: any }[] = [
  { id: 'theme',    label: 'ธีม & สี',  icon: faPalette   },
  { id: 'system',   label: 'ระบบ',       icon: faCog       },
  { id: 'finance',  label: 'การเงิน',    icon: faMoneyBill },
  { id: 'features', label: 'สลิป',   icon: faReceipt   },
];

const SystemSettingsPage: React.FC = () => {
  const [settings, setSettings] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<any>({});
  const [activeTab, setActiveTab] = useState<TabId>('theme');
  const { refreshTheme } = useTheme();

  useEffect(() => { loadSettings(); }, []);

  const loadSettings = async () => {
    try {
      const response = await merchantApi.get('/admin/settings');
      setSettings(response.data.data || []);
      const values: any = {};
      response.data.data.forEach((setting: any) => { values[setting.setting_key] = setting.setting_value; });
      setEditValues(values);
    } catch { toast.error('ไม่สามารถโหลดข้อมูลได้'); }
    finally { setLoading(false); }
  };

  const handleSave = async (settingKey: string, valueOverride?: string) => {
    setSaving(settingKey);
    try {
      const value = valueOverride !== undefined ? valueOverride : editValues[settingKey];
      await merchantApi.put('/admin/settings', { setting_key: settingKey, setting_value: value });
      toast.success('บันทึกสำเร็จ!');
      loadSettings();
    } catch { toast.error('เกิดข้อผิดพลาด'); }
    finally { setSaving(null); }
  };

  const handleSaveAllTheme = async () => {
    setSaving('all_theme');
    try {
      const themeSettings = Object.entries(editValues)
        .filter(([key]) => key.startsWith('theme_'))
        .reduce((acc, [key, value]) => ({ ...acc, [key.replace('theme_', '')]: value }), {});
      await merchantApi.post('/admin/system/theme-settings', { settings: themeSettings });
      toast.success('บันทึกการตั้งค่า Theme สำเร็จ!');
      await refreshTheme();
    } catch { toast.error('เกิดข้อผิดพลาด'); }
    finally { setSaving(null); }
  };

  const applyPalette = (palette: typeof colorPalettes[0]) => {
    setEditValues({
      ...editValues,
      'theme_primary_color': palette.colors.primary,
      'theme_secondary_color': palette.colors.secondary,
      'theme_accent_color': palette.colors.accent,
      'theme_bg_main_color': palette.colors.bg,
      'theme_text_primary_color': palette.colors.text,
    });
    toast.info(`ใช้ธีม "${palette.name}" แล้ว - กดบันทึกเพื่อใช้งาน`);
  };

  const themeColorSettings  = settings.filter(s => s.setting_key.includes('color'));
  const themeOtherSettings  = settings.filter(s => s.setting_key.startsWith('theme_') && !s.setting_key.includes('color'));
  const systemSettings      = settings.filter(s => !s.setting_key.startsWith('theme_'));
  const otherSystemSettings = systemSettings.filter(s => !HANDLED_SETTING_KEYS.has(s.setting_key));

  const getPresetsForKey = (key: string) => {
    if (key.includes('primary'))   return colorPresets.primary;
    if (key.includes('secondary')) return colorPresets.secondary;
    if (key.includes('accent'))    return colorPresets.accent;
    if (key.includes('bg') || key.includes('sidebar')) return colorPresets.bg;
    if (key.includes('text'))      return colorPresets.text;
    return [...colorPresets.primary, ...colorPresets.accent];
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="spinner"></div></div>;
  }

  return (
    <div className="max-w-full mx-auto">
      {/* Premium Header */}
      <div className="relative overflow-hidden rounded-2xl mb-6 p-6 sm:p-8"
           style={{ background: 'var(--theme-primary-gradient)' }}>
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full blur-3xl"
               style={{ backgroundColor: 'var(--theme-primary)', opacity: 0.3 }}></div>
          <div className="absolute -bottom-10 -left-10 w-40 h-40 rounded-full blur-3xl"
               style={{ backgroundColor: 'var(--theme-secondary)', opacity: 0.3 }}></div>
          <div className="absolute inset-0 opacity-10"
               style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '24px 24px' }}></div>
        </div>
        <div className="relative z-10 flex items-center gap-4">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center shadow-lg"
               style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0.05) 100%)', backdropFilter: 'blur(10px)' }}>
            <FontAwesomeIcon icon={faCog} className="text-2xl text-white" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white">System Settings</h1>
            <p className="text-white/70 text-sm mt-1">ตั้งค่าระบบ Payment Gateway และ Theme</p>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="grid grid-cols-4 gap-1 mb-6 bg-white dark:bg-slate-800 rounded-2xl shadow-theme p-1.5">
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 py-3 sm:py-2.5 px-1 sm:px-3 rounded-xl font-medium transition-all
              ${activeTab === tab.id ? 'text-white shadow-md' : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-200'}`}
            style={activeTab === tab.id ? { background: 'var(--theme-primary-gradient)' } : {}}
          >
            <FontAwesomeIcon icon={tab.icon} className="text-base sm:text-sm" />
            <span className="text-[10px] sm:text-sm leading-tight text-center">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* ═══════════════ THEME TAB ═══════════════ */}
      {activeTab === 'theme' && (
        <>
          {/* Color Palettes */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-theme p-6 mb-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white shadow-lg"
                   style={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)' }}>
                <FontAwesomeIcon icon={faSwatchbook} />
              </div>
              <div>
                <h2 className="font-bold text-gray-800 dark:text-slate-100">Color Palettes</h2>
                <p className="text-xs text-gray-400 dark:text-slate-500">เลือก Palette สำเร็จรูปเพื่อใช้งานเร็ว</p>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
              {colorPalettes.map((palette, idx) => (
                <button key={idx} onClick={() => applyPalette(palette)}
                  className="group p-3 sm:p-4 bg-gray-50 dark:bg-slate-700/50 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-xl border-2 border-transparent hover:border-orange-300 transition-all duration-200 text-left active:scale-95">
                  <div className="flex gap-0.5 sm:gap-1 mb-2 sm:mb-3">
                    {Object.values(palette.colors).map((color, i) => (
                      <div key={i} className="flex-1 h-6 sm:h-8 first:rounded-l-md last:rounded-r-md shadow-sm" style={{ backgroundColor: color }} />
                    ))}
                  </div>
                  <p className="text-xs sm:text-sm font-semibold text-gray-700 dark:text-slate-200 group-hover:text-orange-600 transition-colors">{palette.name}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Theme Colors */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-theme p-6 mb-6">
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white shadow-lg flex-shrink-0"
                     style={{ background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' }}>
                  <FontAwesomeIcon icon={faPalette} />
                </div>
                <div>
                  <h2 className="font-bold text-gray-800 dark:text-slate-100">Theme Colors</h2>
                  <p className="text-xs text-gray-400 dark:text-slate-500">ปรับแต่งสีของระบบแต่ละส่วน</p>
                </div>
              </div>
              <button onClick={handleSaveAllTheme} disabled={saving === 'all_theme'}
                className="px-4 sm:px-5 py-2 sm:py-2.5 text-white rounded-xl font-medium text-sm hover:shadow-lg transition-all disabled:opacity-50 flex items-center gap-2 flex-shrink-0"
                style={{ background: 'var(--theme-primary-gradient)' }}>
                {saving === 'all_theme' ? <FontAwesomeIcon icon={faRotate} className="animate-spin" /> : <FontAwesomeIcon icon={faSave} />}
                <span className="hidden sm:inline">บันทึก Theme ทั้งหมด</span>
                <span className="sm:hidden">บันทึก Theme</span>
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {themeColorSettings.map((setting) => (
                <ColorPicker key={setting.id}
                  label={setting.description || setting.setting_key}
                  value={editValues[setting.setting_key] || '#ffffff'}
                  presets={getPresetsForKey(setting.setting_key)}
                  onChange={(color) => setEditValues({ ...editValues, [setting.setting_key]: color })} />
              ))}
            </div>
            <div className="mt-6 p-4 sm:p-5 rounded-xl border-2 border-dashed border-gray-200 dark:border-slate-600">
              <p className="text-xs font-semibold text-gray-500 dark:text-slate-400 mb-3 flex items-center gap-2">
                <FontAwesomeIcon icon={faSliders} /> Live Preview
              </p>
              <div className="flex flex-wrap items-center gap-2 sm:gap-4">
                <button className="px-5 py-2.5 rounded-xl text-white font-semibold text-sm shadow-lg"
                  style={{ background: `linear-gradient(135deg, ${editValues['theme_primary_color'] || '#ff6b35'} 0%, ${editValues['theme_secondary_color'] || '#f7931e'} 100%)` }}>
                  Primary Button
                </button>
                <div className="px-5 py-2.5 rounded-xl text-white font-semibold text-sm shadow-lg"
                     style={{ backgroundColor: editValues['theme_accent_color'] || '#667eea' }}>Accent Color</div>
                <div className="px-5 py-2.5 rounded-xl border-2 font-semibold text-sm"
                     style={{ backgroundColor: editValues['theme_bg_main_color'] || '#f5f7fa', borderColor: editValues['theme_primary_color'] || '#ff6b35', color: editValues['theme_text_primary_color'] || '#1f2937' }}>
                  Sample Text
                </div>
                <div className="px-5 py-2.5 rounded-xl font-semibold text-sm text-white"
                     style={{ backgroundColor: editValues['theme_bg_sidebar_color'] || '#1a1a2e' }}>Sidebar BG</div>
              </div>
            </div>
          </div>

          {/* Other Theme Settings */}
          {themeOtherSettings.length > 0 && (
            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-theme p-6 mb-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center text-white shadow-lg">
                  <FontAwesomeIcon icon={faFont} />
                </div>
                <div>
                  <h2 className="font-bold text-gray-800 dark:text-slate-100">Theme Settings อื่นๆ</h2>
                  <p className="text-xs text-gray-400 dark:text-slate-500">Font, border-radius และค่า theme อื่นๆ</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {themeOtherSettings.map((setting) => (
                  <div key={setting.id} className="p-4 bg-gray-50 dark:bg-slate-700/50 rounded-xl">
                    <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1.5">{setting.description || setting.setting_key}</label>
                    <div className="flex gap-2">
                      <input type="text" value={editValues[setting.setting_key] || ''}
                        onChange={(e) => setEditValues({ ...editValues, [setting.setting_key]: e.target.value })}
                        className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200" />
                      <button onClick={() => handleSave(setting.setting_key)} disabled={saving === setting.setting_key}
                        className="px-4 py-2.5 text-white rounded-xl text-sm transition-colors"
                        style={{ background: 'var(--theme-primary)' }}>
                        <FontAwesomeIcon icon={saving === setting.setting_key ? faRotate : faSave}
                          className={saving === setting.setting_key ? 'animate-spin' : ''} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* ═══════════════ SYSTEM TAB ═══════════════ */}
      {activeTab === 'system' && (
        <>
          {/* System Logo */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-theme p-6 mb-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center text-white shadow-lg">
                <FontAwesomeIcon icon={faImage} />
              </div>
              <div>
                <h2 className="font-bold text-gray-800 dark:text-slate-100">System Logo</h2>
                <p className="text-xs text-gray-400 dark:text-slate-500">Logo ที่แสดงในหน้า Payment และระบบ</p>
              </div>
            </div>
            <div className="flex flex-col md:flex-row gap-6">
              <div className="w-full md:w-56 h-36 bg-gray-50 dark:bg-slate-700 rounded-xl flex items-center justify-center p-4 border-2 border-dashed border-gray-200 dark:border-slate-600">
                {editValues['system_logo_url'] ? (
                  <img src={editValues['system_logo_url']} alt="Logo" className="max-w-full max-h-full object-contain" />
                ) : (
                  <div className="text-center">
                    <FontAwesomeIcon icon={faImage} className="text-3xl text-gray-300 dark:text-slate-600 mb-2" />
                    <p className="text-gray-400 dark:text-slate-500 text-xs">ไม่มี Logo</p>
                  </div>
                )}
              </div>
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1.5">URL หรือ Base64</label>
                <div className="flex flex-col sm:flex-row gap-2">
                  <input type="text" value={editValues['system_logo_url'] || ''}
                    onChange={(e) => setEditValues({ ...editValues, 'system_logo_url': e.target.value })}
                    placeholder="https://example.com/logo.png"
                    className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200" />
                  <button onClick={() => handleSave('system_logo_url')} disabled={saving === 'system_logo_url'}
                    className="px-5 py-2.5 text-white rounded-xl text-sm font-medium transition-all flex items-center justify-center gap-2"
                    style={{ background: 'var(--theme-primary-gradient)' }}>
                    <FontAwesomeIcon icon={saving === 'system_logo_url' ? faRotate : faSave} className={saving === 'system_logo_url' ? 'animate-spin' : ''} />
                    บันทึก
                  </button>
                </div>
                <p className="text-[10px] text-gray-400 dark:text-slate-500 mt-2">รองรับ URL รูปภาพ หรือ Base64 encoded image</p>
              </div>
            </div>
          </div>

          {/* Turnstile CAPTCHA */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-theme p-6 mb-6">
            <div className="flex flex-wrap items-start gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center text-white shadow-lg flex-shrink-0">
                <FontAwesomeIcon icon={faShieldAlt} />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="font-bold text-gray-800 dark:text-slate-100">Turnstile CAPTCHA</h2>
                <p className="text-xs text-gray-400 dark:text-slate-500">ป้องกัน Bot และ Brute-force attacks ด้วย Cloudflare Turnstile</p>
              </div>
              <a href="https://dash.cloudflare.com/?to=/:account/turnstile" target="_blank" rel="noopener noreferrer"
                className="px-3 py-1.5 sm:px-4 sm:py-2 bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 text-gray-600 dark:text-slate-300 rounded-xl text-xs sm:text-sm font-medium transition-colors flex items-center gap-1.5">
                <FontAwesomeIcon icon={faExternalLinkAlt} /> <span className="hidden sm:inline">Cloudflare </span>Dashboard
              </a>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
              <p className="text-sm text-amber-800 mb-2"><strong>📝 วิธีตั้งค่า Turnstile:</strong></p>
              <ol className="text-xs text-amber-700 list-decimal list-inside space-y-1">
                <li>ไปที่ <a href="https://dash.cloudflare.com/?to=/:account/turnstile" target="_blank" rel="noopener noreferrer" className="underline font-medium">Cloudflare Dashboard → Turnstile</a></li>
                <li>สร้าง Site ใหม่ (Widget type: Managed)</li>
                <li>คัดลอก <strong>Site Key</strong> มาใส่ในช่องด้านล่าง</li>
                <li>คัดลอก <strong>Secret Key</strong> ไปใส่ใน Environment Variable (<code className="bg-amber-100 px-1 rounded">TURNSTILE_SECRET_KEY</code>) บน Server</li>
              </ol>
            </div>
            <div className="p-4 bg-gray-50 dark:bg-slate-700/50 rounded-xl">
              <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1.5">Turnstile Site Key (Public)</label>
              <div className="flex flex-col sm:flex-row gap-2">
                <input type="text" value={editValues['turnstile_site_key'] || ''}
                  onChange={(e) => setEditValues({ ...editValues, 'turnstile_site_key': e.target.value })}
                  placeholder="0x4XXXXXXXXXXXXXXXXXXXXXXXX"
                  className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm font-mono focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200" />
                <button onClick={() => handleSave('turnstile_site_key')} disabled={saving === 'turnstile_site_key'}
                  className="px-5 py-2.5 text-white rounded-xl text-sm font-medium transition-all flex items-center justify-center gap-2"
                  style={{ background: 'var(--theme-primary-gradient)' }}>
                  <FontAwesomeIcon icon={saving === 'turnstile_site_key' ? faRotate : faSave} className={saving === 'turnstile_site_key' ? 'animate-spin' : ''} />
                  บันทึก
                </button>
              </div>
              <p className="text-[10px] text-gray-400 dark:text-slate-500 mt-2">
                💡 ถ้าไม่กรอก = ปิดการใช้งาน CAPTCHA | เมื่อกรอกแล้ว ระบบจะแสดง CAPTCHA ในหน้า Login
              </p>
            </div>
            {editValues['turnstile_site_key'] && (
              <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-xl">
                <p className="text-sm text-green-800 flex items-center gap-2">
                  <FontAwesomeIcon icon={faShieldAlt} />
                  <strong>✅ Turnstile CAPTCHA เปิดใช้งานแล้ว</strong>
                </p>
                <p className="text-xs text-green-600 mt-1">หน้า Login ทุกหน้าจะแสดง CAPTCHA เพื่อป้องกัน Bot</p>
              </div>
            )}
          </div>

          {/* Sound Settings */}
          <SoundSettings
            depositUrl={editValues['sound_deposit_url'] || ''}
            withdrawalUrl={editValues['sound_withdrawal_url'] || ''}
            onUploadSuccess={() => loadSettings()}
          />

          {/* Other System Settings (catch-all for unhandled DB settings) */}
          {otherSystemSettings.length > 0 && (
            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-theme p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white shadow-lg"
                     style={{ background: 'var(--theme-primary-gradient)' }}>
                  <FontAwesomeIcon icon={faCog} />
                </div>
                <div>
                  <h2 className="font-bold text-gray-800 dark:text-slate-100">การตั้งค่าอื่นๆ</h2>
                  <p className="text-xs text-gray-400 dark:text-slate-500">Settings อื่นที่ไม่มีหน้าตั้งค่าเฉพาะ</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {otherSystemSettings.map((setting) => {
                  const isPercentage = setting.setting_key.includes('percentage') || setting.setting_key.includes('fee');
                  const isMoney = setting.setting_key.includes('amount') || setting.setting_key.includes('settlement');
                  const isBoolean = setting.setting_key.includes('enabled') || setting.setting_key.includes('_on') || setting.setting_key.includes('_off');
                  return (
                    <div key={setting.id} className="p-4 bg-gray-50 dark:bg-slate-700/50 rounded-xl hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors">
                      <div className="flex items-center justify-between mb-1.5">
                        <label className="text-xs font-medium text-gray-500 dark:text-slate-400">{setting.description || setting.setting_key}</label>
                        {isPercentage && <span className="px-2 py-0.5 bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400 rounded text-[10px] font-medium"><FontAwesomeIcon icon={faPercent} /> Percentage</span>}
                        {isMoney && <span className="px-2 py-0.5 bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400 rounded text-[10px] font-medium"><FontAwesomeIcon icon={faMoneyBill} /> Money</span>}
                        {isBoolean && <span className="px-2 py-0.5 bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400 rounded text-[10px] font-medium">เปิด/ปิด</span>}
                      </div>
                      <div className="flex gap-2">
                        {isBoolean ? (
                          <select value={editValues[setting.setting_key] || 'false'}
                            onChange={(e) => setEditValues({ ...editValues, [setting.setting_key]: e.target.value })}
                            className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 bg-white dark:bg-slate-700 dark:border-slate-600 dark:text-slate-200 cursor-pointer">
                            <option value="true">เปิด (Enabled)</option>
                            <option value="false">ปิด (Disabled)</option>
                          </select>
                        ) : (
                          <input type={isPercentage || isMoney ? 'number' : 'text'} step={isPercentage ? '0.01' : '1'}
                            value={editValues[setting.setting_key] || ''}
                            onChange={(e) => setEditValues({ ...editValues, [setting.setting_key]: e.target.value })}
                            className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200" />
                        )}
                        <button onClick={() => handleSave(setting.setting_key)} disabled={saving === setting.setting_key}
                          className="px-4 py-2.5 text-white rounded-xl text-sm transition-colors"
                          style={{ background: 'var(--theme-primary)' }}>
                          <FontAwesomeIcon icon={saving === setting.setting_key ? faRotate : faSave} className={saving === setting.setting_key ? 'animate-spin' : ''} />
                        </button>
                      </div>
                      <p className="text-[10px] text-gray-400 dark:text-slate-500 mt-1.5">อัพเดทล่าสุด: {new Date(setting.updated_at).toLocaleString('th-TH')}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}

      {/* ═══════════════ FINANCE TAB ═══════════════ */}
      {activeTab === 'finance' && (
        <FinanceControlSection
          editValues={editValues}
          setEditValues={setEditValues}
          saving={saving}
          onSave={handleSave}
        />
      )}

      {/* ═══════════════ FEATURES TAB ═══════════════ */}
      {activeTab === 'features' && (
        <>
          {/* Customer Slip Upload */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-theme p-6 mb-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center text-white shadow-lg">
                <FontAwesomeIcon icon={faReceipt} />
              </div>
              <div>
                <h2 className="font-bold text-gray-800 dark:text-slate-100">ลูกค้าแนบสลิปหน้าจ่ายเงิน</h2>
                <p className="text-xs text-gray-400 dark:text-slate-500">เปิด-ปิดให้ลูกค้าแนบสลิปยืนยันการชำระเงินในหน้า Payment</p>
              </div>
            </div>
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 dark:bg-slate-700/50 rounded-xl">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <FontAwesomeIcon icon={editValues['customer_slip_upload_enabled'] === 'true' ? faToggleOn : faToggleOff}
                      className={`text-2xl mt-0.5 flex-shrink-0 ${editValues['customer_slip_upload_enabled'] === 'true' ? 'text-green-500' : 'text-gray-400'}`} />
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-slate-200">เปิดให้ลูกค้าแนบสลิป</label>
                      <p className="text-xs text-gray-400 dark:text-slate-500 mt-0.5">เมื่อเปิด ลูกค้าจะเห็นปุ่มแนบสลิปในหน้าชำระเงิน (รับเฉพาะสลิปไม่เกิน 15 นาที, ป้องกันสลิปซ้ำ)</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 self-end sm:self-auto flex-shrink-0">
                    <button onClick={() => setEditValues({ ...editValues, 'customer_slip_upload_enabled': editValues['customer_slip_upload_enabled'] === 'true' ? 'false' : 'true' })}
                      className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors ${editValues['customer_slip_upload_enabled'] === 'true' ? 'bg-green-500' : 'bg-gray-300'}`}>
                      <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform ${editValues['customer_slip_upload_enabled'] === 'true' ? 'translate-x-8' : 'translate-x-1'}`} />
                    </button>
                    <button onClick={() => handleSave('customer_slip_upload_enabled')} disabled={saving === 'customer_slip_upload_enabled'}
                      className="px-4 sm:px-5 py-2 sm:py-2.5 text-white rounded-xl text-sm transition-colors flex items-center gap-2"
                      style={{ background: 'var(--theme-primary)' }}>
                      <FontAwesomeIcon icon={saving === 'customer_slip_upload_enabled' ? faRotate : faSave} className={saving === 'customer_slip_upload_enabled' ? 'animate-spin' : ''} />
                      บันทึก
                    </button>
                  </div>
                </div>
              </div>
              {editValues['customer_slip_upload_enabled'] === 'true' ? (
                <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
                  <p className="text-sm text-green-800 flex items-center gap-2">
                    <FontAwesomeIcon icon={faCheck} />
                    <strong>เปิดใช้งานแล้ว</strong> - ลูกค้าสามารถแนบสลิปในหน้าชำระเงินได้
                  </p>
                </div>
              ) : (
                <div className="p-4 bg-gray-100 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-xl">
                  <p className="text-sm text-gray-600 dark:text-slate-300 flex items-center gap-2">
                    <FontAwesomeIcon icon={faToggleOff} />
                    <strong>ปิดอยู่</strong> - ลูกค้าจะไม่เห็นปุ่มแนบสลิปในหน้าชำระเงิน
                  </p>
                </div>
              )}

              {/* Allow slip overpay */}
              <div className="p-4 bg-gray-50 dark:bg-slate-700/50 rounded-xl">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <FontAwesomeIcon icon={editValues['allow_slip_overpay'] === 'true' ? faToggleOn : faToggleOff}
                      className={`text-2xl mt-0.5 flex-shrink-0 ${editValues['allow_slip_overpay'] === 'true' ? 'text-green-500' : 'text-gray-400'}`} />
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-slate-200">อนุญาตรับสลิปยอดเกิน (Overpay)</label>
                      <p className="text-xs text-gray-400 dark:text-slate-500 mt-0.5">เมื่อเปิด ระบบจะรับสลิปที่ยอด ≥ ยอดที่ต้องชำระ แล้วปรับ order/กระเป๋า/ค่าคอมตามยอดสลิปจริง — มีผลทุกช่องทาง (ลูกค้า, merchant, admin, API) · ยอดน้อยกว่าดู toggle Underpay ด้านล่าง</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 self-end sm:self-auto flex-shrink-0">
                    <button onClick={() => setEditValues({ ...editValues, 'allow_slip_overpay': editValues['allow_slip_overpay'] === 'true' ? 'false' : 'true' })}
                      className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors ${editValues['allow_slip_overpay'] === 'true' ? 'bg-green-500' : 'bg-gray-300'}`}>
                      <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform ${editValues['allow_slip_overpay'] === 'true' ? 'translate-x-8' : 'translate-x-1'}`} />
                    </button>
                    <button onClick={() => handleSave('allow_slip_overpay')} disabled={saving === 'allow_slip_overpay'}
                      className="px-4 sm:px-5 py-2 sm:py-2.5 text-white rounded-xl text-sm transition-colors flex items-center gap-2"
                      style={{ background: 'var(--theme-primary)' }}>
                      <FontAwesomeIcon icon={saving === 'allow_slip_overpay' ? faRotate : faSave} className={saving === 'allow_slip_overpay' ? 'animate-spin' : ''} />
                      บันทึก
                    </button>
                  </div>
                </div>
              </div>

              {/* Allow slip underpay */}
              <div className="p-4 bg-gray-50 dark:bg-slate-700/50 rounded-xl">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <FontAwesomeIcon icon={editValues['allow_slip_underpay'] === 'true' ? faToggleOn : faToggleOff}
                      className={`text-2xl mt-0.5 flex-shrink-0 ${editValues['allow_slip_underpay'] === 'true' ? 'text-green-500' : 'text-gray-400'}`} />
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-slate-200">อนุญาตรับสลิปยอดขาด (Underpay)</label>
                      <p className="text-xs text-gray-400 dark:text-slate-500 mt-0.5">เมื่อเปิด ระบบจะรับสลิปยอดน้อยกว่ายอดที่ต้องชำระ (ต้องมากกว่า 0) แล้วปรับ order/กระเป๋า/ค่าคอมลงตามยอดสลิปจริง — มีผลทุกช่องทางรวมเติมเงิน Payout (ไม่รวม P2P deposit) ⚠️ order จะถูกปิดเป็น paid ทันทีที่ยอดแรกเข้า โอนเพิ่มทีหลังไม่ได้</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 self-end sm:self-auto flex-shrink-0">
                    <button onClick={() => setEditValues({ ...editValues, 'allow_slip_underpay': editValues['allow_slip_underpay'] === 'true' ? 'false' : 'true' })}
                      className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors ${editValues['allow_slip_underpay'] === 'true' ? 'bg-green-500' : 'bg-gray-300'}`}>
                      <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform ${editValues['allow_slip_underpay'] === 'true' ? 'translate-x-8' : 'translate-x-1'}`} />
                    </button>
                    <button onClick={() => handleSave('allow_slip_underpay')} disabled={saving === 'allow_slip_underpay'}
                      className="px-4 sm:px-5 py-2 sm:py-2.5 text-white rounded-xl text-sm transition-colors flex items-center gap-2"
                      style={{ background: 'var(--theme-primary)' }}>
                      <FontAwesomeIcon icon={saving === 'allow_slip_underpay' ? faRotate : faSave} className={saving === 'allow_slip_underpay' ? 'animate-spin' : ''} />
                      บันทึก
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Slip Verification */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-theme p-6 mb-6">
            {/* Header */}
            <div className="flex flex-wrap items-start gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center text-white shadow-lg flex-shrink-0">
                <FontAwesomeIcon icon={faReceipt} />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="font-bold text-gray-800 dark:text-slate-100">ตรวจสอบสลิป</h2>
                <p className="text-xs text-gray-400 dark:text-slate-500">ตั้งค่าการตรวจสอบสลิปก่อนอนุมัติรายการ</p>
              </div>
              {(editValues['slip_verification_provider'] || 'slip2go') === 'slip2go' ? (
                <a href="https://slip2go.com" target="_blank" rel="noopener noreferrer"
                  className="px-3 py-1.5 sm:px-4 sm:py-2 bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 text-gray-600 dark:text-slate-300 rounded-xl text-xs sm:text-sm font-medium transition-colors flex items-center gap-1.5">
                  <FontAwesomeIcon icon={faExternalLinkAlt} /> Slip2Go
                </a>
              ) : (
                <a href="https://easyslip.com" target="_blank" rel="noopener noreferrer"
                  className="px-3 py-1.5 sm:px-4 sm:py-2 bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 text-gray-600 dark:text-slate-300 rounded-xl text-xs sm:text-sm font-medium transition-colors flex items-center gap-1.5">
                  <FontAwesomeIcon icon={faExternalLinkAlt} /> EasySlip
                </a>
              )}
            </div>

            <div className="space-y-4">
              {/* Enable toggle */}
              <div className="p-4 bg-gray-50 dark:bg-slate-700/50 rounded-xl">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <FontAwesomeIcon icon={editValues['slip_verification_enabled'] === 'true' ? faToggleOn : faToggleOff}
                      className={`text-2xl mt-0.5 flex-shrink-0 ${editValues['slip_verification_enabled'] === 'true' ? 'text-green-500' : 'text-gray-400'}`} />
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-slate-200">ตรวจสอบสลิปก่อนอัพโหลด</label>
                      <p className="text-xs text-gray-400 dark:text-slate-500 mt-0.5">เปิดใช้งานเพื่อตรวจสอบสลิปผ่าน API ก่อนอนุมัติ</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 self-end sm:self-auto flex-shrink-0">
                    <button onClick={() => setEditValues({ ...editValues, 'slip_verification_enabled': editValues['slip_verification_enabled'] === 'true' ? 'false' : 'true' })}
                      className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors ${editValues['slip_verification_enabled'] === 'true' ? 'bg-green-500' : 'bg-gray-300'}`}>
                      <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform ${editValues['slip_verification_enabled'] === 'true' ? 'translate-x-8' : 'translate-x-1'}`} />
                    </button>
                    <button onClick={() => handleSave('slip_verification_enabled')} disabled={saving === 'slip_verification_enabled'}
                      className="px-4 sm:px-5 py-2 sm:py-2.5 text-white rounded-xl text-sm transition-colors flex items-center gap-2"
                      style={{ background: 'var(--theme-primary)' }}>
                      <FontAwesomeIcon icon={saving === 'slip_verification_enabled' ? faRotate : faSave} className={saving === 'slip_verification_enabled' ? 'animate-spin' : ''} />
                      บันทึก
                    </button>
                  </div>
                </div>
              </div>

              {/* Provider selector */}
              <div className="p-4 bg-gray-50 dark:bg-slate-700/50 rounded-xl">
                <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-3">เลือก Provider ตรวจสอบสลิป</label>
                <div className="flex gap-3 mb-3">
                  {(['slip2go', 'easyslip'] as const).map((p) => {
                    const isActive = (editValues['slip_verification_provider'] || 'slip2go') === p;
                    return (
                      <button key={p}
                        onClick={() => setEditValues({ ...editValues, 'slip_verification_provider': p })}
                        className={`flex-1 py-2.5 px-4 rounded-xl text-sm font-medium border-2 transition-all ${isActive ? 'border-cyan-500 bg-cyan-50 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300' : 'border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-500 dark:text-slate-400 hover:border-gray-300'}`}>
                        {p === 'slip2go' ? 'Slip2Go' : 'EasySlip'}
                      </button>
                    );
                  })}
                </div>
                <div className="flex justify-end">
                  <button onClick={() => handleSave('slip_verification_provider')} disabled={saving === 'slip_verification_provider'}
                    className="px-5 py-2.5 text-white rounded-xl text-sm font-medium transition-all flex items-center gap-2"
                    style={{ background: 'var(--theme-primary-gradient)' }}>
                    <FontAwesomeIcon icon={saving === 'slip_verification_provider' ? faRotate : faSave} className={saving === 'slip_verification_provider' ? 'animate-spin' : ''} />
                    บันทึก
                  </button>
                </div>
              </div>

              {/* Provider-specific key input */}
              {(editValues['slip_verification_provider'] || 'slip2go') === 'slip2go' ? (
                <div className="p-4 bg-gray-50 dark:bg-slate-700/50 rounded-xl">
                  <label className="flex items-center gap-2 text-xs font-medium text-gray-500 dark:text-slate-400 mb-1.5">
                    <FontAwesomeIcon icon={faKey} className="text-gray-400 dark:text-slate-500" /> Slip2Go Secret Key
                  </label>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <input type="password" value={editValues['slip2go_secret_key'] || ''}
                      onChange={(e) => setEditValues({ ...editValues, 'slip2go_secret_key': e.target.value })}
                      placeholder="ใส่ Secret Key จาก Slip2Go"
                      className="flex-1 px-4 py-2.5 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-xl text-sm font-mono focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500" />
                    <button onClick={() => handleSave('slip2go_secret_key')} disabled={saving === 'slip2go_secret_key'}
                      className="px-5 py-2.5 text-white rounded-xl text-sm font-medium transition-all flex items-center justify-center gap-2"
                      style={{ background: 'var(--theme-primary-gradient)' }}>
                      <FontAwesomeIcon icon={saving === 'slip2go_secret_key' ? faRotate : faSave} className={saving === 'slip2go_secret_key' ? 'animate-spin' : ''} />
                      บันทึก
                    </button>
                  </div>
                  <p className="text-[10px] text-gray-400 dark:text-slate-500 mt-2">💡 รับ Secret Key ได้จาก Slip2Go Dashboard หลังลงทะเบียน</p>
                </div>
              ) : (
                <div className="p-4 bg-gray-50 dark:bg-slate-700/50 rounded-xl">
                  <label className="flex items-center gap-2 text-xs font-medium text-gray-500 dark:text-slate-400 mb-1.5">
                    <FontAwesomeIcon icon={faKey} className="text-gray-400 dark:text-slate-500" /> EasySlip API Key
                  </label>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <input type="password" value={editValues['easyslip_api_key'] || ''}
                      onChange={(e) => setEditValues({ ...editValues, 'easyslip_api_key': e.target.value })}
                      placeholder="ใส่ API Key จาก EasySlip"
                      className="flex-1 px-4 py-2.5 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-xl text-sm font-mono focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500" />
                    <button onClick={() => handleSave('easyslip_api_key')} disabled={saving === 'easyslip_api_key'}
                      className="px-5 py-2.5 text-white rounded-xl text-sm font-medium transition-all flex items-center justify-center gap-2"
                      style={{ background: 'var(--theme-primary-gradient)' }}>
                      <FontAwesomeIcon icon={saving === 'easyslip_api_key' ? faRotate : faSave} className={saving === 'easyslip_api_key' ? 'animate-spin' : ''} />
                      บันทึก
                    </button>
                  </div>
                  <p className="text-[10px] text-gray-400 dark:text-slate-500 mt-2">💡 รับ API Key ได้จาก EasySlip Dashboard หลังลงทะเบียน</p>
                </div>
              )}

              {/* Status card */}
              {(() => {
                const provider = editValues['slip_verification_provider'] || 'slip2go';
                const providerName = provider === 'easyslip' ? 'EasySlip' : 'Slip2Go';
                const activeKey = provider === 'easyslip' ? editValues['easyslip_api_key'] : editValues['slip2go_secret_key'];
                if (editValues['slip_verification_enabled'] === 'true') {
                  if (activeKey) {
                    return (
                      <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
                        <p className="text-sm text-green-800 flex items-center gap-2">
                          <FontAwesomeIcon icon={faCheck} />
                          <strong>✅ ระบบตรวจสอบสลิปเปิดใช้งานแล้ว ({providerName})</strong>
                        </p>
                        <p className="text-xs text-green-600 mt-1">สลิปจะถูกตรวจสอบผ่าน {providerName} API ก่อนอนุมัติรายการทุกครั้ง</p>
                      </div>
                    );
                  }
                  return (
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                      <p className="text-sm text-amber-800 flex items-center gap-2">
                        <FontAwesomeIcon icon={faKey} />
                        <strong>⚠️ กรุณาใส่ {providerName} Key</strong>
                      </p>
                      <p className="text-xs text-amber-600 mt-1">เปิดใช้งานแล้ว แต่ยังไม่ได้ใส่ Key ระบบจะไม่สามารถตรวจสอบสลิปได้</p>
                    </div>
                  );
                }
                return (
                  <div className="p-4 bg-gray-100 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-xl">
                    <p className="text-sm text-gray-600 dark:text-slate-300 flex items-center gap-2">
                      <FontAwesomeIcon icon={faToggleOff} />
                      <strong>ระบบตรวจสอบสลิปปิดอยู่</strong>
                    </p>
                    <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">การอัพโหลดสลิปจะไม่ถูกตรวจสอบผ่าน API (อนุมัติได้ทันที)</p>
                  </div>
                );
              })()}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SystemSettingsPage;
